import { useState, useEffect } from 'react'
import { Tag, Button, Loading } from 'react-vant'
import GlobalNoticeBar from '../../components/GlobalNoticeBar'
import { FriendsO, CalendarO, ShieldO, AppsO, ChatO, Arrow } from '@react-vant/icons'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { logout } from '../../store/userSlice'
import request from '../../api/request'
import teacherAffairsApi from '../../api/teacherAffairs'
import '../home/style.css'

export default function Home() {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { userInfo } = useSelector(state => state.user)

    const [loading, setLoading] = useState(true)
    const [adminOverview, setAdminOverview] = useState(null)
    const [affairsStats, setAffairsStats] = useState({
        duty: 0, overtime: 0, leave: 0, balance: 0
    })
    const [upcomingMatches, setUpcomingMatches] = useState([])

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)
            try {
                const now = new Date()
                const curMonth = now.getMonth() + 1
                const curYear = now.getFullYear()
                
                // 调整为学年统计维度 (9月至次年8月)
                let yearStart, yearEnd
                if (curMonth >= 9) {
                    yearStart = `${curYear}-09-01`
                    yearEnd = `${curYear + 1}-08-31`
                } else {
                    yearStart = `${curYear - 1}-09-01`
                    yearEnd = `${curYear}-08-31`
                }

                // 并行获取待办事项和教师事务数据
                const [pendingRes, matchRes, affairsRes] = await Promise.allSettled([
                    request.get('/sports/registrations/pending-approval/', { params: { page_size: 1 } }).catch(() => ({ total: 0 })),
                    request.get('/sports/class-match/').catch(() => []),
                    Promise.allSettled([
                        teacherAffairsApi.getDutySchedules({ teacher_id: userInfo?.id, start_date: yearStart, end_date: yearEnd }),
                        teacherAffairsApi.getOvertimeStats({ start_date: yearStart, end_date: yearEnd }),
                        teacherAffairsApi.getLeaveStats({ start_date: yearStart, end_date: yearEnd }),
                        userInfo?.id ? teacherAffairsApi.getLeaveBalance(userInfo.id) : Promise.resolve([]),
                    ])
                ])

                // 处理近期比赛
                const matchData = matchRes.status === 'fulfilled' ? matchRes.value : []
                if (Array.isArray(matchData)) {
                    const upcoming = matchData
                        .filter(m => m.status === 'scheduled' || m.status === 'ongoing')
                        .sort((a, b) => new Date(a.match_date) - new Date(b.match_date))
                        .slice(0, 3)
                    setUpcomingMatches(upcoming)
                }

                // 处理教师事务统计
                if (affairsRes.status === 'fulfilled') {
                    const [duty, ot, leave, balance] = affairsRes.value
                    const dutyCount = duty.status === 'fulfilled' ? (duty.value?.length || 0) : 0
                    const otHours = ot.status === 'fulfilled' ? (ot.value?.total_hours || 0) : 0
                    const leaveDays = leave.status === 'fulfilled' ? (leave.value?.total_days || 0) : 0
                    const balances = balance.status === 'fulfilled' ? (balance.value || []) : []
                    const totalBalance = Array.isArray(balances) ? balances.reduce((sum, b) => sum + (b.remaining_days || 0), 0) : 0

                    setAffairsStats({
                        duty: dutyCount,
                        overtime: Math.round(otHours * 10) / 10,
                        leave: Math.round(leaveDays * 10) / 10,
                        balance: Math.round(totalBalance * 10) / 10,
                    })
                }

            } catch (e) {
                console.error('获取首页数据失败:', e)
            } finally {
                setLoading(false)
            }
        }
        fetchData()
    }, [userInfo?.id])

    // admin 是纯管理账号 → 首页显示「管理速览」而不是「个人事务看板」
    const isAdminUser = userInfo?.role === 'admin'

    // admin 拉管理速览（全校待办与概览）
    useEffect(() => {
        if (!isAdminUser) return;
        teacherAffairsApi.getAdminOverview()
            .then(res => setAdminOverview(res?.data || res || null))
            .catch(() => {});
    }, [isAdminUser]);
    const adminCards = [
        { key: 'pending_leave_count', label: '待审批请假', emoji: '✅', path: '/teacher/affairs/admin/pending-leave', unit: '条' },
        { key: 'pending_substitute_count', label: '待安排代课', emoji: '🔁', path: '/teacher/affairs/admin/pending-substitute', unit: '节' },
        { key: 'overtime_hours_month', label: '本月加班', emoji: '⏰', path: '/teacher/affairs', unit: 'h' },
        { key: 'leave_days_month', label: '本月请假', emoji: '📝', path: '/teacher/affairs', unit: '天' },
    ]

    const affairsCards = [
        { key: 'duty', label: '值班次数', emoji: '📅', path: '/teacher/affairs/duty', unit: '次' },
        { key: 'overtime', label: '加班时长', emoji: '⏰', path: '/teacher/affairs/overtime', unit: 'h' },
        { key: 'leave', label: '请假天数', emoji: '📝', path: '/teacher/affairs/leave', unit: '天' },
        { key: 'balance', label: '免假天数', emoji: '🛡️', path: '/teacher/affairs', unit: '天' },
    ]

    return (
        <div className="home-page" style={{ background: 'var(--canvas-bg, #f8fafc)', minHeight: '100vh', paddingBottom: 90 }}>
            {/* ========== 顶部 Hero 导航头 (App 风格青蓝渐变) ========== */}
            <div style={{
                background: 'var(--primary-gradient, linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%))',
                padding: '48px 20px 80px',
                color: '#fff',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* 装饰半透圆 */}
                <div style={{
                    position: 'absolute', top: -30, right: -30,
                    width: 140, height: 140, borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.08)'
                }} />

                <div style={{ display: 'flex', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', marginBottom: 4 }}>下午好 👋</div>
                        <h3 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: '-0.3px', color: '#fff' }}>
                            {userInfo?.real_name || '教师'}
                        </h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
                            <div style={{
                                display: 'inline-flex', alignItems: 'center',
                                background: 'rgba(255,255,255,0.18)',
                                backdropFilter: 'blur(4px)',
                                padding: '2px 8px', borderRadius: 10, fontSize: 11
                            }}>
                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#34d399', marginRight: 6 }}></span>
                                {userInfo?.school_name || '北京市丰台区东高地第二小学'}
                            </div>
                        </div>
                    </div>
                    <div style={{
                        width: 54, height: 54, borderRadius: '50%',
                        background: 'rgba(255,255,255,0.25)',
                        border: '2px solid rgba(255,255,255,0.4)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 22, fontWeight: 800, color: '#fff',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}>
                        {userInfo?.real_name?.[0] || '教'}
                    </div>
                </div>
            </div>

            {/* ========== 内容容器 (悬浮层叠布局) ========== */}
            <div style={{ marginTop: '-48px', padding: '0 16px', position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* 顶部全局系统通告滚动栏 */}
                <GlobalNoticeBar />

                {/* 1. 个人事务看板卡片 */}
                <div style={{
                    background: 'var(--card-bg, #ffffff)',
                    borderRadius: 18,
                    padding: '20px',
                    border: '1px solid var(--border-subtle, #e2e8f0)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.03)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 4, height: 15, background: 'var(--primary-color, #056b4f)', borderRadius: 2 }} />
                            <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>
                                {isAdminUser ? '管理速览' : '个人事务看板'}
                            </h4>
                        </div>
                        <div
                            onClick={() => navigate('/teacher/affairs')}
                            style={{ fontSize: 12, color: 'var(--primary-color, #056b4f)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
                        >
                            进入大厅 <Arrow style={{ fontSize: 10 }} />
                        </div>
                    </div>

                    {/* 四列数据统计 */}
                    <div style={{
                        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8,
                        borderBottom: '1px solid #f1f5f9', paddingBottom: 18, marginBottom: 18
                    }}>
                        {(isAdminUser ? adminCards : affairsCards).map(card => (
                            <div
                                key={card.key}
                                onClick={() => navigate(card.path)}
                                style={{
                                    textAlign: 'center', padding: '8px 4px', borderRadius: 12,
                                    background: '#f8fafc', cursor: 'pointer'
                                }}
                            >
                                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary, #0f172a)', marginBottom: 2 }}>
                                    {loading ? <Loading size={14} />
                                        : (isAdminUser ? (adminOverview?.[card.key] ?? 0) : affairsStats[card.key])}
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--text-tertiary, #94a3b8)' }}>
                                    {card.label}/{card.unit}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* 通用办公工具 */}
                    <div style={{
                        fontSize: 14, fontWeight: 700, color: 'var(--text-primary, #0f172a)',
                        marginBottom: 12
                    }}>
                        常用办公工具
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                        {[
                            ...(userInfo?.role === 'admin' ? [{ icon: <ChatO style={{ fontSize: 22 }} />, text: '指挥中心', path: '/admin', color: '#7c3aed', bg: '#f5f3ff' }] : []),
                            { icon: <FriendsO style={{ fontSize: 22 }} />, text: '学生管理', path: '/teacher/classes', color: '#056b4f', bg: '#e8f6f0' },
                            { icon: <AppsO style={{ fontSize: 22 }} />, text: '社团管理', path: '/clubs/teacher', color: '#00b578', bg: '#ecfdf5' },
                            { icon: <CalendarO style={{ fontSize: 22 }} />, text: '值班安排', path: '/teacher/affairs/duty', color: '#ff5e1e', bg: '#fff7ed' },
                        ].map((action, idx) => (
                            <div
                                key={idx}
                                onClick={() => action.path.startsWith('http') ? (window.location.href = action.path) : (action.path !== '#' && navigate(action.path))}
                                style={{ textAlign: 'center', cursor: 'pointer' }}
                            >
                                <div style={{ 
                                    width: 48, height: 48, borderRadius: 14, 
                                    background: action.bg, 
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', 
                                    margin: '0 auto 8px',
                                    color: action.color
                                }}>
                                    {action.icon}
                                </div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary, #475569)' }}>{action.text}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 2. 近期比赛卡片 */}
                <div style={{
                    background: 'var(--card-bg, #ffffff)',
                    borderRadius: 18,
                    padding: '18px 20px',
                    border: '1px solid var(--border-subtle, #e2e8f0)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.03)'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 4, height: 15, background: 'var(--athletic-color, #ff5e1e)', borderRadius: 2 }} />
                            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>近期比赛</span>
                        </div>
                        <div
                            onClick={() => navigate('/sports/class-match')}
                            style={{ color: 'var(--primary-color, #056b4f)', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
                        >
                            全部赛事 <Arrow style={{ fontSize: 10 }} />
                        </div>
                    </div>

                    {upcomingMatches.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {upcomingMatches.map(match => (
                                <div
                                    key={match.id}
                                    onClick={() => navigate(`/sports/class-match/${match.id}`)}
                                    style={{ 
                                        borderRadius: 14, padding: '12px 14px',
                                        background: '#f8fafc', border: '1px solid #f1f5f9',
                                        display: 'flex', alignItems: 'center', cursor: 'pointer'
                                    }}
                                >
                                    <div style={{ 
                                        width: 44, height: 44, borderRadius: 12, 
                                        background: match.status === 'ongoing' ? '#ecfdf5' : '#e8f6f0',
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                        marginRight: 14, flexShrink: 0
                                    }}>
                                        <div style={{ fontSize: 10, color: 'var(--text-tertiary, #94a3b8)', fontWeight: 600 }}>
                                            {new Date(match.match_date).getMonth() + 1}月
                                        </div>
                                        <div style={{ fontSize: 16, fontWeight: 800, color: match.status === 'ongoing' ? '#00b578' : '#056b4f' }}>
                                            {new Date(match.match_date).getDate()}
                                        </div>
                                    </div>
                                    <div style={{ flex: 1, overflow: 'hidden' }}>
                                        <div style={{
                                            fontSize: 14, fontWeight: 700, color: 'var(--text-primary, #0f172a)',
                                            marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                        }}>
                                            {match.title}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <Tag size="mini" type={match.status === 'ongoing' ? 'success' : 'primary'} style={{
                                                borderRadius: 6,
                                                background: match.status === 'ongoing' ? '#ecfdf5' : '#e8f6f0',
                                                border: 'none',
                                                color: match.status === 'ongoing' ? '#00b578' : '#056b4f',
                                                padding: '1px 6px'
                                            }}>
                                                {match.status === 'ongoing' ? '进行中' : '待开始'}
                                            </Tag>
                                            <span style={{ fontSize: 11, color: 'var(--text-tertiary, #94a3b8)' }}>
                                                {match.grade || '班级赛'} · {match.venue || '学校操场'}
                                            </span>
                                        </div>
                                    </div>
                                    <Arrow style={{ color: '#cbd5e1', fontSize: 14 }} />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '28px 0', color: 'var(--text-tertiary, #94a3b8)', fontSize: 13 }}>
                            <div style={{ fontSize: 28, marginBottom: 4 }}>🏆</div>
                            近期暂无安排中的比赛
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
